import * as React from 'react';
import { connect } from 'react-redux';
import { Modal, Button, Form, FormGroup, ControlLabel, FormControl } from 'react-bootstrap';
import { Translate } from '../../../../shared/LanguageService';
import FormInputChangeHandler from '../../../../shared/form/FormInputChangeHandler';
import { handleError } from '../../../../shared/ErrorCatching';
import FormSelect from '../../../../components/common/FormSelect';
import * as Actions from './GenericOrderActions';
import notify from '../../../../shared/feedback/notifications';
import { getSiteName } from '../../../../shared/Site';
import * as Site from '../../../../shared/Site';

interface ICreateGenericOrderModalProps {
    orderCollectionId: number;
    showModal: boolean;
    closeModal: () => void;
    closeAndNavigate: (path: string) => void;
    actions: ICreateGenericOrderModalActions;
    genericOrder: {
        customers: ICustomer[];
        brokers: IBroker[];
        legalEntities: ILegalEntity[];
        sites: ISite[];
        orderCollectionName: string;
    };
    site: string;
}

interface ICreateGenericOrderModalActions {
    new: (orderCollectionId: number) => any;
    create: (genericIrder: ICreateGenericOrder) => any;
}

interface ILegalEntity {
    id: number;
    name: string;
}

interface ISite {
    id: number;
    name: string;
}

interface ICustomer {
    id: number;
    name: string;
}

interface IBroker {
    id: number;
    name: string;
    customers: number[];
}

interface ICreateGenericOrder {
    orderCollectionId?: number;
    brokerAgentId: number;
    brokerFirmId: number;
    legalEntityId: number;
    siteId?: number;
    orderCollectionName: string;
}

interface ICreateGenericOrderResponse {
    orderId: number;
}

class CreateGenericOrderModal extends React.Component<ICreateGenericOrderModalProps, any> {
    formInputChangeHandler: FormInputChangeHandler;

    constructor(props: ICreateGenericOrderModalProps) {
        super(props);
        this.state = {
            customer: '-1',
            broker: '-1',
            legalEntity: '-1',
            site: '-1',
            orderCollectionName: ''
        };
        this.setValue = this.setValue.bind(this);
        this.brokerAgentCanBookForCustomer = this.brokerAgentCanBookForCustomer.bind(this);
        this.onSave = this.onSave.bind(this);
        this.formInputChangeHandler = new FormInputChangeHandler(this.state, this.setValue);
    }

    componentDidMount(): void {
        this.props.actions.new(this.props.orderCollectionId).catch(handleError);
    }

    setValue(newState: any) {
        this.setState(newState);
    }

    onSave() {
        this.props.actions
            .create({
                orderCollectionId: this.props.orderCollectionId,
                brokerFirmId: this.state.customer,
                brokerAgentId: this.state.broker,
                legalEntityId: this.state.legalEntity,
                siteId: this.state.site === -1 ? null : this.state.site,
                orderCollectionName: this.state.orderCollectionName
            })
            .then((response: ICreateGenericOrderResponse) => {
                notify.success(Translate('generic-order/toastr/success'));
                this.props.closeAndNavigate(`/orders/${response.orderId}`);
            })
            .catch(handleError);
    }

    disableSaveButton(): boolean {
        return !(
            parseInt(this.state.customer) > 0 &&
            parseInt(this.state.broker) > 0 &&
            parseInt(this.state.legalEntity) > 0 &&
            (this.props.orderCollectionId ||
                (parseInt(this.state.site) > 0 && this.state.orderCollectionName))
        );
    }

    disableBrokerAgentList(): boolean {
        return !(this.state.customer && parseInt(this.state.customer) > 0);
    }

    brokerAgentCanBookForCustomer(broker: IBroker): boolean {
        return this.state.customer && broker.customers.indexOf(parseInt(this.state.customer)) >= 0;
    }

    render() {
        return (
            <Modal
                show={this.props.showModal}
                onHide={this.props.closeModal}
                autoFocus={false}
                enforceFocus={false}
                backdrop={'static'}
            >
                <Modal.Header>
                    <Button
                        className="close-x pull-right"
                        bsStyle="link"
                        onClick={this.props.closeModal}
                        style={{padding: '5px 0 0 0'}}
                    >
                        <i style={{margin: '0'}} className="material-icons">close</i>
                    </Button>
                    <Modal.Title>{Translate('create-generic-order-modal/title')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        {!this.props.orderCollectionId && (
                            <FormGroup>
                                <ControlLabel>
                                    {Translate('create-generic-order-modal/site/label')}
                                </ControlLabel>
                                <FormSelect
                                    name="site"
                                    value={this.state.site}
                                    onChange={this.formInputChangeHandler.onChange}
                                >
                                    <option key={-1} value="-1">
                                        {Translate(
                                            'create-generic-order-modal/site/select/default'
                                        )}
                                    </option>
                                    {this.props.site === getSiteName(Site.Zentuvo) ?
                                        <option value={"1"} key={1}>
                                            {"Zentuvo"}
                                        </option> : 
                                        <>
                                           {this.props.genericOrder.sites.map((site) => (
                                                <option value={site.id.toString()} key={site.id}>
                                                    {site.name}
                                                </option>
                                                ))
                                           }
                                        </>
                                    }
                                </FormSelect>
                            </FormGroup>
                        )}
                        <FormGroup>
                            <ControlLabel>
                                {Translate('create-generic-order-modal/customer/label')}
                            </ControlLabel>
                            <FormSelect
                                name="customer"
                                value={this.state.customer}
                                onChange={this.formInputChangeHandler.onChange}
                            >
                                <option key={-1} value="-1">
                                    {Translate(
                                        'create-generic-order-modal/customer/select/default'
                                    )}
                                </option>
                                {this.props.genericOrder.customers.map((customer) => (
                                    <option value={customer.id.toString()} key={customer.id}>
                                        {customer.name}
                                    </option>
                                ))}
                            </FormSelect>
                        </FormGroup>
                        <FormGroup>
                            <ControlLabel>
                                {Translate('create-generic-order-modal/broker/label')}
                            </ControlLabel>
                            <FormSelect
                                disabled={this.disableBrokerAgentList()}
                                name="broker"
                                value={this.state.broker}
                                onChange={this.formInputChangeHandler.onChange}
                            >
                                <option key={-1} value="-1">
                                    {Translate('create-generic-order-modal/broker/select/default')}
                                </option>
                                {this.props.genericOrder.brokers
                                    .filter(this.brokerAgentCanBookForCustomer)
                                    .map((broker) => (
                                        <option value={broker.id.toString()} key={broker.id}>
                                            {broker.name}
                                        </option>
                                    ))}
                            </FormSelect>
                        </FormGroup>
                        <FormGroup>
                            <ControlLabel>
                                {Translate('create-generic-order-modal/legal-entity/label')}
                            </ControlLabel>
                            <FormSelect
                                name="legalEntity"
                                value={this.state.legalEntity}
                                onChange={this.formInputChangeHandler.onChange}
                            >
                                <option key={-1} value="-1">
                                    {Translate(
                                        'create-generic-order-modal/legal-entity/select/default'
                                    )}
                                </option>
                                {this.props.genericOrder.legalEntities.map((legalEntity) => (
                                    <option value={legalEntity.id.toString()} key={legalEntity.id}>
                                        {legalEntity.name}
                                    </option>
                                ))}
                            </FormSelect>
                        </FormGroup>
                        {!this.props.orderCollectionId && (
                            <FormGroup>
                                <ControlLabel>
                                    {Translate(
                                        'create-generic-order-modal/order-collection-name/label'
                                    )}
                                </ControlLabel>
                                <FormControl
                                    type="text"
                                    name="orderCollectionName"
                                    value={this.state.ordeCollectionName}
                                    onChange={this.formInputChangeHandler.onChange}
                                />
                            </FormGroup>
                        )}
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button onClick={this.props.closeModal}>
                        {Translate('create-generic-order-modal/close-button')}
                    </Button>
                    <Button
                        bsStyle="primary"
                        onClick={this.onSave}
                        disabled={this.disableSaveButton()}
                    >
                        {Translate('create-generic-order-modal/save-button')}
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    }
}

function mapStateToProps(state: any, ownProps: any) {
    return {
        genericOrder: state.genericOrder.model,
                        site: state.site
    };
}

function mapDispatchToProps(dispatch: any) {
    return {
        actions: {
            new: (orderCollectionId: number) =>
                dispatch(Actions.newGenericOrder(orderCollectionId)),
            create: (genericOrder: ICreateGenericOrder) => Actions.createGenericOrder(genericOrder)
        }
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(CreateGenericOrderModal);
